import type { ComponentType } from "react"
import PromotionPopup000 from "./promotions/PromotionPopup000"
import PromotionPopup418 from "./promotions/PromotionPopup418"
import PromotionPopup445 from "./promotions/PromotionPopup445"
import PromotionPopup475 from "./promotions/PromotionPopup475"

export interface PromotionEventPopupComponentProps {
  onClose: () => void
  /** 완료된 액션 타입 (미션 이벤트는 액션별 문구 분기용, 나머지는 무시) */
  action?: EventActionType
  /** 확인 버튼 라벨 (기본값: "확인") */
  confirmLabel?: string
  /** 확인 시 이벤트 페이지로 이동 (사용하지 않는 컴포넌트는 무시) */
  navigateToEvent?: boolean
}

/** 이벤트 코드 매핑에 사용하는 실행 환경 (L: 로컬, D: 개발, S: 검증, P: 운영) */
export type PromotionEnv = "L" | "D" | "S" | "P"

/** 이벤트 완료 조건에 사용되는 액션 타입 */
export type EventActionType = "book" | "sentence" | "recommendation" | "home"

export type PromotionEventComponentPopupConfig = {
  type: "component"
  component: ComponentType<PromotionEventPopupComponentProps>
  defaultExpireDate?: string // 기본 만료 날짜 (yyyy-mm-dd 형식)
  actions: EventActionType[] // 이벤트 완료에 필요한 액션 목록
  /**
   * 액션 완료 시 팝업 노출 시점
   * - "or":   어느 하나 완료 시 팝업 (1회)
   * - "and":  모두 완료 시 팝업 (1회, 기본값)
   * - "each": 각 액션 완료 시마다 팝업 (n회, 전체 완료 시에만 제거)
   */
  actionsMode?: "or" | "and" | "each"
  headerEventCodeByEnv?: Partial<Record<PromotionEnv, string>>
}

export type PromotionEventPopupConfig = PromotionEventComponentPopupConfig

export type PromotionEventPopupDefinitions = Record<
  string,
  PromotionEventPopupConfig
>

// URL/query로 들어온 이벤트 코드를 내부 canonical 이벤트 코드로 정규화한다.
// headerEventCodeByEnv는 API 헤더 송신용 매핑이므로 이 입력 alias와 별개다.
// "475" 하드코딩은 이 파일에만 존재 (config 키·alias·headerEventCodeByEnv·컴포넌트 URL).
export const PROMOTION_EVENT_CODE_ALIASES: Record<string, string> = {
  "966": "445",
  "982": "475",
}

export const resolvePromotionEventCode = (eventCode: string): string =>
  PROMOTION_EVENT_CODE_ALIASES[eventCode] ?? eventCode

/** NEXT_PUBLIC_ROOT hostname 기반 실행 환경 감지 (L/D/S/P) */
export const getPromotionEnvFromRoot = (
  root?: string,
): PromotionEnv | undefined => {
  if (!root) return undefined
  try {
    const hostname = new URL(root).hostname
    if (hostname === "library.kyobo.com") return "P"
    if (hostname === "libraryt.kyobo.com") return "S"
    if (hostname === "libraryd.kyobo.com") return "D"
    return "L"
  } catch {
    return undefined
  }
}

/**
 * 환경별 이벤트 헤더 코드를 canonical 코드로 변환.
 * config.headerEventCodeByEnv[env]이 있으면 해당 코드, 없으면 canonical 코드 반환.
 */
export const resolvePromotionEventHeaderCode = (eventCode: string): string => {
  const config = PROMOTION_EVENT_POPUP_CONFIG[eventCode]
  if (!config?.headerEventCodeByEnv) return eventCode

  const env = getPromotionEnvFromRoot(process.env.NEXT_PUBLIC_ROOT)
  return env ? (config.headerEventCodeByEnv[env] ?? eventCode) : eventCode
}

/**
 * 프로모션 이벤트 팝업 정의
 *
 * 새 이벤트 추가 시:
 * 1. 팝업 컴포넌트 등록
 * 2. actions: 완료 조건 액션 배열 (예: ["book"], ["book", "sentence"])
 * 3. actionsMode: "and" (모두 완료) | "or" (하나만 완료) - 기본값 "and"
 *
 * 사용처: checkEvent("book") - 해당 액션을 가진 모든 활성 이벤트 자동 처리
 */
export const PROMOTION_EVENT_POPUP_CONFIG: PromotionEventPopupDefinitions = {
  "000": {
    type: "component",
    component: PromotionPopup000,
    defaultExpireDate: "2025-12-14",
    actions: ["book"],
    actionsMode: "or", // 하나만 완료해도 팝업
  },
  "418": {
    type: "component",
    component: PromotionPopup418,
    defaultExpireDate: "2026-02-28",
    actions: ["book", "sentence"],
    actionsMode: "and", // 둘 다 완료해야 팝업
    // 내부 이벤트 키와 API 헤더 이벤트 코드가 환경별로 다를 때만 매핑한다.
    headerEventCodeByEnv: {
      D: "418",
      S: "918",
    },
  },
  "445": {
    type: "component",
    component: PromotionPopup445,
    defaultExpireDate: "2026-06-30",
    actions: ["home"],
    actionsMode: "or", // 메인 페이지 도착 시 팝업
    // 같은 프로모션이라도 환경별 이벤트 코드가 다를 수 있어 API 헤더 송신값만 매핑한다.
    headerEventCodeByEnv: {
      L: "445",
      D: "966",
      S: "966",
      P: "445",
    },
  },
  // 미션 플로우: 도서 등록(미션1) + 추천사 등록(미션2) 각 완료 시 개별 팝업
  "475": {
    type: "component",
    component: PromotionPopup475,
    defaultExpireDate: "2026-10-21",
    actions: ["book", "recommendation"],
    // 각 액션 완료 시마다 팝업 (dedup으로 각 1회), 전체 완료 시에만 제거
    actionsMode: "each",
    headerEventCodeByEnv: {
      L: "475",
      D: "982",
      S: "982",
      P: "475",
    },
  },
}
